import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useScroll, useSpring } from 'framer-motion';
import { Mail, Menu, Phone, X } from 'lucide-react';
import { COMPANY_INFO, LOGO_URL } from '../../data/company';
import { Button } from '../ui/button';

const NAV_LINKS = [
  { name: 'Home', href: '/' },
  { name: 'Products', href: '/products' },
  { name: 'Industries', href: '/industries' },
  { name: 'About', href: '/about' },
  { name: 'Support', href: '/support' },
  { name: 'Contact', href: '/contact' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const progressScale = useSpring(scrollYProgress, { stiffness: 160, damping: 28, mass: 0.2 });
  const phoneNumbers = COMPANY_INFO.contact.phone || [];
  const primaryEmail = COMPANY_INFO.contact.email?.[0];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <div className="hidden border-b border-white/10 bg-[#06101f] py-2 text-sm text-slate-200 md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            {phoneNumbers.map((phone) => (
              <a key={phone} href={`tel:${phone}`} className="flex items-center gap-2 transition-colors hover:text-[#60A5FA]">
                <Phone className="h-3.5 w-3.5" />
                <span>{phone}</span>
              </a>
            ))}
            {primaryEmail && (
              <a href={`mailto:${primaryEmail}`} className="flex items-center gap-2 transition-colors hover:text-[#60A5FA]">
                <Mail className="h-3.5 w-3.5" />
                <span>{primaryEmail}</span>
              </a>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="trust-chip">{COMPANY_INFO.certification}</span>
            <span className="trust-chip">{COMPANY_INFO.stats.installations} Installations</span>
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 border-b border-white/10 bg-[#08152b]/95 backdrop-blur transition-all duration-300 ${
          isScrolled ? 'shadow-[0_20px_55px_rgba(2,6,23,0.45)]' : 'shadow-sm'
        }`}
        data-testid="main-header"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-24 items-center justify-between py-3">
            <Link
              to="/"
              className="flex items-center rounded-sm border border-white/15 bg-white px-4 py-2 shadow-[0_12px_30px_rgba(2,6,23,0.28)] transition-transform hover:-translate-y-0.5"
              data-testid="header-logo"
            >
              <img
                src={LOGO_URL}
                alt="LEION Engineering Logo"
                className="h-16 w-auto object-contain"
              />
            </Link>

            <nav className="hidden items-center gap-1 lg:flex" data-testid="desktop-nav">
              {NAV_LINKS.map((link) => (
                <div key={link.name} className="relative group">
                  <Link
                    to={link.href}
                    className={`relative block rounded-sm px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-all duration-200 active:scale-[0.98] ${
                      (link.href === '/products' && location.pathname.startsWith('/products')) || location.pathname === link.href
                        ? 'bg-[#1E40AF] text-white'
                        : 'text-slate-200 hover:bg-white/10 hover:text-[#60A5FA]'
                    }`}
                    data-testid={`nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                    {((link.href === '/products' && location.pathname.startsWith('/products')) || location.pathname === link.href) && (
                      <motion.span
                        layoutId="active-nav-underline"
                        className="absolute inset-x-3 -bottom-1 h-0.5 bg-[#93C5FD]"
                        transition={{ type: 'spring', stiffness: 450, damping: 34 }}
                      />
                    )}
                  </Link>
                </div>
              ))}
            </nav>

            <div className="hidden items-center gap-4 lg:flex">
              <Link to="/contact#quote-form">
                <Button
                  className="tech-shimmer rounded-sm bg-[#0369A1] px-6 py-5 font-bold uppercase tracking-wider text-white shadow-[0_18px_45px_rgba(3,105,161,0.35)] transition-all hover:-translate-y-0.5 hover:bg-[#075985] active:scale-[0.98]"
                  data-testid="get-quote-btn"
                >
                  Get a Quote
                </Button>
              </Link>
            </div>

            <button
              className="p-2 lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle navigation menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-6 w-6 text-white" /> : <Menu className="h-6 w-6 text-white" />}
            </button>
          </div>
        </div>

        <motion.div
          className="absolute bottom-0 left-0 h-[2px] w-full origin-left bg-gradient-to-r from-[#60A5FA] via-[#2563EB] to-[#38BDF8]"
          style={{ scaleX: progressScale }}
        />

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="border-t border-white/10 bg-[#08152b] lg:hidden"
              data-testid="mobile-menu"
            >
              <div className="space-y-4 px-4 py-6">
                {NAV_LINKS.map((link) => (
                  <div key={link.name}>
                    <Link
                      to={link.href}
                      className={`block rounded-sm px-2 py-2 font-semibold transition-colors ${
                        (link.href === '/products' && location.pathname.startsWith('/products')) || location.pathname === link.href
                          ? 'bg-[#1E40AF] text-white'
                          : 'text-slate-200 hover:bg-white/10'
                      }`}
                    >
                      {link.name}
                    </Link>
                  </div>
                ))}
                <div className="pt-4">
                  <Link to="/contact#quote-form" className="block">
                    <Button className="w-full rounded-sm bg-[#0369A1] py-4 font-bold uppercase tracking-wider text-white hover:bg-[#075985]">
                      Get a Quote
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;
